import { Grid } from '@mui/material';
import { useEffect, useState, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { Dropmenu, Search } from '../../Components';
import { Link, useNavigate } from 'react-router-dom';
import Loading from '../../Components/Loading';
import PostAddIcon from '@mui/icons-material/PostAdd';
import StyledDashboardLayout from '../style';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { Box, Typography } from '@mui/material';
import {
   useGetAddonServicesQuery,
   useDeleteAddonServiceMutation,
} from '../../Redux/Service/AddonServiceApi';

const AddOnList = () => {
   const MySwal = withReactContent(Swal);
   const navigate = useNavigate();
   const [addonData, setAddonData] = useState(null);
   const [searchInput, setSearchInput] = useState('');
   const [searchQuery, setSearchQuery] = useState('');
   const [page, setPage] = useState(1);
   const [rowsPerPage, setRowsPerPage] = useState(10);
   const [loading, setLoading] = useState(true);

   const customStyles = {
      rows: {
         style: {
            margin: '0px',
            padding: 0,
         },
      },
      headCells: {
         style: {
            margin: '0px',
            padding: '10px 15px',
            fontWeight: 600,
            fontSize: 15,
            background: '#3F30FF1A',
         },
      },
      cells: {
         style: {
            margin: '0px',
            padding: '10px 15px',
            fontSize: 15,
         },
      },
   };

   const {
      data: addons,
      isLoading,
      isError,
      error,
   } = useGetAddonServicesQuery({
      limit: rowsPerPage,
      page,
      search: searchQuery,
   });

   const [deleteAddonService] = useDeleteAddonServiceMutation();

   // Debounce searchInput
   useEffect(() => {
      const timer = setTimeout(() => {
         setSearchQuery(searchInput);
         setPage(1);
      }, 500);

      return () => clearTimeout(timer);
   }, [searchInput]);

   // Synchronize RTK query data to local state
   useEffect(() => {
      if (!isLoading && !isError) {
         setAddonData(addons?.response);
         setLoading(false);
      }
   }, [isLoading, isError, addons]);

   // Show error toast if query fails
   useEffect(() => {
      if (isError && error) {
         toast.error(error?.data?.message || 'Failed to fetch services');
      }
   }, [isError, error]);

   const handleEdit = (id) => {
      navigate(`/services-for-sell-edit/${id}`);
   };

   const handleView = (id) => {
      navigate(`/services-for-sell/${id}`);
   };

   const handleDelete = async (id) => {
      const result = await MySwal.fire({
         title: 'Are you sure?',
         text: 'Are you sure you want to delete this Service?',
         icon: 'warning',
         showCancelButton: true,
         confirmButtonText: 'Yes, delete it!',
         cancelButtonText: 'Cancel',
      });
      if (!result.isConfirmed) return;

      try {
         await deleteAddonService(id).unwrap();
         await MySwal.fire('Deleted!', 'Service has been deleted.', 'success');

         // Handle pagination when last item on page is deleted
         const currentCount = addonData?.data?.length || 0;
         if (currentCount === 1 && page > 1) {
            setPage((prev) => prev - 1);
         }
      } catch (error) {
         console.error('Error deleting service:', error);
         const msg = error?.data?.message || 'Error deleting service';
         await MySwal.fire('Error!', msg, 'error');
      }
   };

   const handleSearch = useCallback((search) => {
      setSearchInput(search);
   }, []);

   const columns = [
      {
         name: 'SL No',
         cell: (row, index) => (page - 1) * rowsPerPage + index + 1,
         button: false,
      },
      { name: 'Name', selector: (row) => row.name, sortable: true, wrap: true },
      { name: 'Type', selector: (row) => row.type, sortable: true },
      {
         name: 'Value',
         selector: (row) => (row.type === 'percentage' ? `${row.value}%` : row.value),
         sortable: true,
      },
      {
         name: 'Description',
         selector: (row) => row.description || '-',
         wrap: true,
      },
      {
         name: 'Active',
         selector: (row) => (row.is_active ? 'Active' : 'Inactive'),
      },
      {
         name: 'Created By',
         selector: (row) => row.created_by || '-',
         sortable: true,
         wrap: true,
         cell: (row) => (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
               {row.created_by || '-'}
            </div>
         ),
      },
      {
         name: 'Created At',
         selector: (row) => (row.created_at ? new Date(row.created_at).toLocaleString() : '-'),
         sortable: true,
      },
      {
         name: 'Action',
         cell: (row) => (
            <div>
               <Dropmenu
                  handleEdit={() => handleEdit(row.id)}
                  handleView={() => handleView(row.id)}
                  handleDelete={() => handleDelete(row.id)}
               />
            </div>
         ),
         button: false,
      },
   ];

   if (isLoading) return <Loading />;

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between mb-2">
                     <div className="pageTitle">
                        <h1 className="m-0">Services</h1>
                     </div>
                     <div className="btnWrap">
                        <Link to="/services-for-sell-add" className="btn addBtn">
                           <PostAddIcon /> Add Service
                        </Link>
                     </div>
                  </div>
               </div>
            </div>

            <div className="card m-3 customCard">
               <div>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                     <Grid item md={6} xs={12}></Grid>
                     <Grid item md={6} xs={12} display="flex" justifyContent="flex-end">
                        <Search onSearch={(query) => handleSearch(query)} />
                     </Grid>
                  </Grid>

                  {isError ? (
                     <Box
                        sx={{
                           display: 'flex',
                           flexDirection: 'column',
                           alignItems: 'center',
                           justifyContent: 'center',
                           py: 8,
                           gap: 2,
                           backgroundColor: '#fee',
                           borderRadius: 2,
                           border: '1px solid #fcc',
                           p: 4,
                        }}
                     >
                        <Typography
                           variant="body2"
                           sx={{ color: '#666', textAlign: 'center', maxWidth: 400 }}
                        >
                           {error?.data?.message ||
                              error?.message ||
                              'Something went wrong while fetching services. Please try again.'}
                        </Typography>
                     </Box>
                  ) : (
                     <DataTable
                        key={page}
                        columns={columns}
                        data={addonData?.data || []}
                        pagination
                        paginationServer
                        paginationTotalRows={addonData?.total || 0}
                        paginationDefaultPage={page}
                        paginationPerPage={rowsPerPage}
                        onChangePage={(p) => setPage(p)}
                        onChangeRowsPerPage={(newPerPage) => {
                           setRowsPerPage(newPerPage);
                           setPage(1);
                        }}
                        customStyles={customStyles}
                        className="dataTable"
                        progressPending={isLoading}
                        progressComponent={
                           <Box sx={{ p: 3, textAlign: 'center' }}>
                              <Typography>Loading data...</Typography>
                           </Box>
                        }
                        noDataComponent={
                           <Box
                              sx={{
                                 display: 'flex',
                                 flexDirection: 'column',
                                 alignItems: 'center',
                                 justifyContent: 'center',
                                 py: 6,
                                 gap: 1,
                              }}
                           >
                              <Typography variant="h6" sx={{ color: '#999' }}>
                                 No services found
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#bbb' }}>
                                 Try adjusting your search criteria
                              </Typography>
                           </Box>
                        }
                     />
                  )}
               </div>
            </div>
         </div>
      </StyledDashboardLayout>
   );
};

export default AddOnList;
